/**
 * LitPet Trade Portal — client behaviour
 *
 * The portal is one page; every module is a panel. This script owns:
 * - hash routing between panels (#dashboard, #catalog, #orders, …)
 * - gate tab switching (sign in / create account / apply)
 * - company/location switcher toggle
 * - quick-order form submit (cart add via /cart/add.js)
 * - asset library category filtering
 * - toast feedback
 * - invoice print
 */

(function () {
  'use strict';

  var VIEWS = [
    'dashboard',
    'catalog',
    'price-list',
    'orders',
    'shipments',
    'purchase-history',
    'invoices',
    'marketing',
    'academy',
    'account'
  ];

  /* ---------------------------------------------------------
     Toast
     --------------------------------------------------------- */
  var toastEl = null;
  var toastTimer = null;

  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'trade-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 2600);
  }

  /* ---------------------------------------------------------
     Hash routing
     --------------------------------------------------------- */
  function viewFromHash() {
    var raw = (window.location.hash || '').replace(/^#/, '');
    if (!raw) return 'dashboard';
    // Anchors like #invoice-123 belong to a panel; map them to their view.
    if (raw.indexOf('invoice-') === 0) return 'invoices';
    if (raw.indexOf('order-') === 0) return 'orders';
    if (raw.indexOf('shipment-') === 0) return 'shipments';
    return VIEWS.indexOf(raw) !== -1 ? raw : 'dashboard';
  }

  function showView(name, options) {
    var opts = options || {};
    var views = document.querySelectorAll('[data-trade-view]');
    if (!views.length) return;

    views.forEach(function (view) {
      var isTarget = view.getAttribute('data-trade-view') === name;
      view.hidden = !isTarget;
      view.classList.toggle('is-active', isTarget);
    });

    document.querySelectorAll('[data-trade-view-link]').forEach(function (link) {
      var active = link.getAttribute('data-trade-view-link') === name;
      if (active) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });

    var sidebar = document.querySelector('.trade-sidebar');
    if (sidebar) sidebar.classList.remove('is-open');

    if (!opts.keepScroll) {
      var top = document.getElementById('TradeContent');
      if (opts.focusTarget) {
        var target = document.getElementById(opts.focusTarget);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          target.classList.add('is-flashed');
          setTimeout(function () {
            target.classList.remove('is-flashed');
          }, 1600);
          return;
        }
      }
      if (top && opts.scrollTop !== false) {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
    }
  }

  function initRouter() {
    if (!document.querySelector('[data-trade-view]')) return;

    showView(viewFromHash());

    window.addEventListener('hashchange', function () {
      showView(viewFromHash());
    });

    // In-page links that switch panel and optionally jump to an element.
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-trade-view-link]');
      if (!link) return;

      var name = link.getAttribute('data-trade-view-link');
      if (VIEWS.indexOf(name) === -1) return;

      var target = link.getAttribute('data-trade-target') || '';
      var nextHash = '#' + (target || name);

      e.preventDefault();
      if (window.location.hash !== nextHash) {
        window.history.pushState(null, '', nextHash);
      }
      showView(name, { focusTarget: target || null, scrollTop: !target });
    });

    // Dashboard order links jump to the order detail block.
    document.querySelectorAll('[data-trade-order-link]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var id = link.getAttribute('data-trade-order-link');
        var target = 'order-detail-' + id;
        e.preventDefault();
        var nextHash = '#' + target;
        if (window.location.hash !== nextHash) window.history.pushState(null, '', nextHash);
        showView('orders', { focusTarget: target, scrollTop: false });
      });
    });

    window.addEventListener('popstate', function () {
      showView(viewFromHash());
    });
  }

  /* ---------------------------------------------------------
     Gate tabs — Apple-like segmented control
     --------------------------------------------------------- */
  var gateCurrent = null;
  var gateTimer = null;
  var gateHeadingTimer = null;
  var gateResizeTimer = null;
  var gateHeights = { signin: 0, apply: 0 };
  var GATE_MS = 420;

  var GATE_COPY = {
    signin: {
      title: 'Sign in to Trade Portal',
      sub: 'For approved UK stockists and distributors.'
    },
    apply: {
      title: 'Apply for trade access',
      sub: 'Tell us about your business. LitPet reviews every application.'
    }
  };

  function setGateHeading(name, instant) {
    var copy = GATE_COPY[name];
    if (!copy) return;
    var title = document.querySelector('[data-trade-gate-title]');
    var sub = document.querySelector('[data-trade-gate-sub]');
    if (!title || !sub) return;
    if (title.textContent === copy.title && sub.textContent === copy.sub) return;

    clearTimeout(gateHeadingTimer);
    if (instant) {
      title.textContent = copy.title;
      sub.textContent = copy.sub;
      title.classList.remove('is-swap');
      sub.classList.remove('is-swap');
      return;
    }

    title.classList.add('is-swap');
    sub.classList.add('is-swap');
    gateHeadingTimer = setTimeout(function () {
      title.textContent = copy.title;
      sub.textContent = copy.sub;
      title.classList.remove('is-swap');
      sub.classList.remove('is-swap');
    }, 90);
  }

  function cacheGateHeights(root) {
    var panes = root.querySelectorAll('[data-trade-gate-pane]');
    panes.forEach(function (pane) {
      var key = pane.getAttribute('data-trade-gate-pane');
      // Panes stay mounted (never display:none) so scrollHeight is free.
      gateHeights[key] = Math.max(1, Math.ceil(pane.scrollHeight));
    });
  }

  function lockPanelsHeight(panels, height) {
    panels.style.height = Math.ceil(height) + 'px';
  }

  function syncGateChrome(root, name) {
    var seg = root.querySelector('.trade-apple__seg');
    root.querySelectorAll('[data-trade-gate-tab]').forEach(function (tab) {
      var on = tab.getAttribute('data-trade-gate-tab') === name;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    if (seg) seg.setAttribute('data-active', name);
  }

  function paintGatePanes(root, name) {
    root.querySelectorAll('[data-trade-gate-pane]').forEach(function (pane) {
      var on = pane.getAttribute('data-trade-gate-pane') === name;
      pane.classList.toggle('is-in', on);
      pane.classList.remove('is-out');
      pane.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
  }

  function activateGateTab(name, instant) {
    var root = document.querySelector('[data-trade-gate-tabs]');
    if (!root) return;
    if (!instant && name === gateCurrent) return;

    var panels = root.querySelector('.trade-apple__panels');
    var next = root.querySelector('[data-trade-gate-pane="' + name + '"]');
    if (!panels || !next) return;

    // Interruptible: clicking mid-animation just retargets — no queue hitch.
    clearTimeout(gateTimer);
    syncGateChrome(root, name);
    setGateHeading(name, !!instant);

    var toH = gateHeights[name] || Math.ceil(next.scrollHeight);
    gateHeights[name] = toH;

    if (instant || !gateCurrent) {
      panels.classList.add('is-instant');
      paintGatePanes(root, name);
      lockPanelsHeight(panels, toH);
      panels.classList.add('is-ready');
      void panels.offsetHeight;
      panels.classList.remove('is-instant');
      gateCurrent = name;
      return;
    }

    panels.classList.add('is-ready');

    var fromH = panels.style.height
      ? parseFloat(panels.style.height)
      : gateHeights[gateCurrent] || Math.ceil(panels.getBoundingClientRect().height);

    // Start from current pixel height, then animate in one frame.
    lockPanelsHeight(panels, fromH || toH);
    paintGatePanes(root, name);

    requestAnimationFrame(function () {
      lockPanelsHeight(panels, toH);
    });

    gateCurrent = name;
    gateTimer = setTimeout(function () {
      lockPanelsHeight(panels, gateHeights[gateCurrent] || toH);
    }, GATE_MS);
  }

  function initGateTabs() {
    var root = document.querySelector('[data-trade-gate-tabs]');
    if (!root || root.dataset.gateReady) return;
    root.dataset.gateReady = '1';

    root.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-trade-gate-tab]');
      if (!tab || !root.contains(tab)) return;
      activateGateTab(tab.getAttribute('data-trade-gate-tab'));
    });

    // Warm both heights once before first interaction.
    cacheGateHeights(root);

    var params = new URLSearchParams(window.location.search);
    var requested = params.get('view') || (window.location.hash || '').replace(/^#/, '');
    if (requested === 'register') requested = 'apply';
    if (requested === 'apply' || requested === 'signin') {
      activateGateTab(requested, true);
    } else if (root.querySelector('.trade-apply-form .trade-alert--ok, .trade-gate__notice')) {
      activateGateTab('apply', true);
    } else {
      activateGateTab(
        (root.querySelector('.trade-apple__seg') || {}).getAttribute('data-active') || 'signin',
        true
      );
    }

    // Recache after fonts/images settle.
    window.addEventListener('load', function () {
      cacheGateHeights(root);
      if (gateCurrent) lockPanelsHeight(root.querySelector('.trade-apple__panels'), gateHeights[gateCurrent]);
    });

    window.addEventListener('resize', function () {
      clearTimeout(gateResizeTimer);
      gateResizeTimer = setTimeout(function () {
        cacheGateHeights(root);
        if (gateCurrent) {
          lockPanelsHeight(root.querySelector('.trade-apple__panels'), gateHeights[gateCurrent]);
        }
      }, 120);
    });
  }

  /* ---------------------------------------------------------
     Mobile sidebar toggle
     --------------------------------------------------------- */
  function initSidebarToggle() {
    var btn = document.querySelector('[data-trade-menu-toggle]');
    var sidebar = document.querySelector('.trade-sidebar');
    if (!btn || !sidebar) return;

    btn.addEventListener('click', function () {
      var open = sidebar.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* ---------------------------------------------------------
     Switcher (company / location)
     --------------------------------------------------------- */
  function initSwitchers() {
    document.querySelectorAll('[data-trade-switcher]').forEach(function (root) {
      var btn = root.querySelector('[data-trade-switcher-toggle]');
      var menu = root.querySelector('[data-trade-switcher-menu]');
      if (!btn || !menu) return;

      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        menu.hidden = open;
      });

      document.addEventListener('click', function () {
        if (btn.getAttribute('aria-expanded') === 'true') {
          btn.setAttribute('aria-expanded', 'false');
          menu.hidden = true;
        }
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && btn.getAttribute('aria-expanded') === 'true') {
          btn.setAttribute('aria-expanded', 'false');
          menu.hidden = true;
          btn.focus();
        }
      });
    });
  }

  /* ---------------------------------------------------------
     Quick order — add a single variant with quantity
     --------------------------------------------------------- */
  function cartAddUrl() {
    return window.Shopify && Shopify.routes && Shopify.routes.cart_add_url
      ? Shopify.routes.cart_add_url
      : '/cart/add.js';
  }

  function addItemsToCart(items, successMessage) {
    return fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          toast(result.data.description || result.data.message || 'Could not add this item.');
          return false;
        }
        toast(successMessage || 'Added to cart');
        refreshCartCount();
        return true;
      })
      .catch(function () {
        toast('Network error — please try again.');
        return false;
      });
  }

  function initVariantSelect() {
    document.querySelectorAll('[data-trade-variant-select]').forEach(function (select) {
      select.addEventListener('change', function () {
        var option = select.options[select.selectedIndex];
        if (!option) return;
        var card = select.closest('[data-trade-card]');
        if (!card) return;

        var form = card.querySelector('[data-trade-order-form]');
        var priceEl = card.querySelector('[data-trade-card-price]');
        var skuEl = card.querySelector('[data-trade-card-sku]');
        var metaEl = card.querySelector('[data-trade-card-meta]');
        var qtyInput = card.querySelector('[data-trade-qty]');
        var btn = card.querySelector('[data-trade-order-submit]');

        var variantId = option.value;
        var money = option.getAttribute('data-price-money') || '';
        var sku = option.getAttribute('data-sku') || '';
        var available = option.getAttribute('data-available') === '1';
        var moq = parseInt(option.getAttribute('data-moq') || '1', 10) || 1;
        var increment = parseInt(option.getAttribute('data-increment') || '1', 10) || 1;
        var inventory = option.getAttribute('data-inventory');

        if (form) form.setAttribute('data-variant-id', variantId);
        if (priceEl) priceEl.textContent = money;
        if (skuEl) skuEl.textContent = sku ? 'SKU ' + sku : 'LitPet';
        if (qtyInput) {
          qtyInput.min = String(moq);
          qtyInput.step = String(increment);
          qtyInput.value = String(moq);
        }
        if (metaEl) {
          if (!available) metaEl.textContent = 'Out of stock';
          else if (moq > 1) {
            metaEl.textContent =
              'Min. ' + moq + (increment > 1 ? ' · steps of ' + increment : '');
          } else if (inventory !== '' && inventory != null) {
            metaEl.textContent = inventory + ' available';
          } else {
            metaEl.textContent = 'In stock';
          }
        }
        if (btn) {
          btn.disabled = !available;
          btn.textContent = available ? 'Add to cart' : 'Unavailable';
        }
      });
    });
  }

  function initQuickOrder() {
    document.querySelectorAll('[data-trade-order-form]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();

        var btn = form.querySelector('[data-trade-order-submit]');
        var qtyInput = form.querySelector('[data-trade-qty]');
        var variantId = form.getAttribute('data-variant-id');
        var qty = parseInt((qtyInput && qtyInput.value) || '0', 10);

        if (!variantId) {
          toast('This product is not available to order.');
          return;
        }
        if (!qty || qty < 1) {
          toast('Enter a quantity of at least 1.');
          if (qtyInput) qtyInput.focus();
          return;
        }

        var label = btn ? btn.textContent : '';
        if (btn) {
          btn.disabled = true;
          btn.textContent = 'Adding…';
        }

        addItemsToCart(
          [{ id: Number(variantId), quantity: qty }],
          qty + ' × ' + (form.getAttribute('data-product-title') || 'item') + ' added to cart'
        ).finally(function () {
          if (btn) {
            btn.disabled = false;
            btn.textContent = label || 'Add to cart';
          }
        });
      });
    });
  }

  function initReorder() {
    document.querySelectorAll('[data-trade-reorder]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var raw = btn.getAttribute('data-items') || '[]';
        var items;
        try {
          items = JSON.parse(raw);
        } catch (err) {
          toast('Could not read reorder lines.');
          return;
        }
        if (!items.length) {
          toast('Nothing to reorder.');
          return;
        }
        var label = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        addItemsToCart(items, 'Previous lines added to cart — add a PO at checkout.')
          .finally(function () {
            btn.disabled = false;
            btn.textContent = label || 'Reorder';
          });
      });
    });
  }

  function refreshCartCount() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-trade-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
      })
      .catch(function () {});
  }

  /* ---------------------------------------------------------
     Catalog filtering (client-side, within the catalog panel)
     --------------------------------------------------------- */
  function initCatalogFilter() {
    var input = document.querySelector('[data-trade-filter]');
    if (!input) return;
    var cards = Array.prototype.slice.call(document.querySelectorAll('[data-trade-card]'));
    var empty = document.querySelector('[data-trade-filter-empty]');

    input.addEventListener('input', function () {
      var q = input.value.trim().toLowerCase();
      var shown = 0;
      cards.forEach(function (card) {
        var hay = (card.getAttribute('data-search') || '').toLowerCase();
        var match = !q || hay.indexOf(q) !== -1;
        card.hidden = !match;
        if (match) shown++;
      });
      if (empty) empty.hidden = shown !== 0;
    });
  }

  /* ---------------------------------------------------------
     Asset library category filter
     --------------------------------------------------------- */
  function initLibraryFilters() {
    var tabs = document.querySelectorAll('[data-trade-library-tabs] [data-trade-category]');
    if (!tabs.length) return;

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var wanted = tab.getAttribute('data-trade-category');
        var group = tab.closest('[data-trade-library-tabs]');
        var grid = group && group.parentElement.querySelector('[data-trade-library-grid]');
        var empty = group && group.parentElement.querySelector('[data-trade-library-empty]');
        if (!grid) return;

        var shown = 0;
        grid.querySelectorAll('[data-trade-asset]').forEach(function (card) {
          var match = !wanted || card.getAttribute('data-category') === wanted;
          card.hidden = !match;
          if (match) shown++;
        });

        tabs.forEach(function (t) {
          if (t === tab) t.setAttribute('aria-current', 'page');
          else t.removeAttribute('aria-current');
        });

        if (empty) empty.hidden = shown !== 0;
      });
    });
  }

  /* ---------------------------------------------------------
     Print invoice
     --------------------------------------------------------- */
  function initPrint() {
    document.querySelectorAll('[data-trade-print]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var target = document.querySelector(btn.getAttribute('data-trade-print'));
        if (!target) {
          window.print();
          return;
        }
        // Make the invoice the only visible block, then print.
        target.style.display = 'block';
        document.body.classList.add('is-printing-invoice');

        var cleanup = function () {
          target.style.display = '';
          document.body.classList.remove('is-printing-invoice');
          window.removeEventListener('afterprint', cleanup);
        };
        window.addEventListener('afterprint', cleanup);

        window.print();
        setTimeout(cleanup, 1200);
      });
    });
  }

  /* ---------------------------------------------------------
     Boot
     --------------------------------------------------------- */
  function boot() {
    initRouter();
    initGateTabs();
    initSidebarToggle();
    initSwitchers();
    initVariantSelect();
    initQuickOrder();
    initReorder();
    initCatalogFilter();
    initLibraryFilters();
    initPrint();
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  document.addEventListener('shopify:section:load', boot);
})();
